<template>
    <Teleport to="body">
      <!-- Blurred Background Overlay - exactly like Header.vue scrolled state -->
      <div
        v-if="toasts.length > 0"
        class="fixed inset-0 z-[9998]"
        style="
          background-color: rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
        "
      ></div>
  
      <!-- Centered Toast Container -->
      <div class="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none p-6">
        <div class="space-y-6 pointer-events-auto max-w-lg w-full">
          <TransitionGroup
            enter-active-class="transform transition-all duration-500 ease-out"
            enter-from-class="translate-y-[-30px] opacity-0 scale-90"
            enter-to-class="translate-y-0 opacity-100 scale-100"
            leave-active-class="transform transition-all duration-300 ease-in"
            leave-from-class="translate-y-0 opacity-100 scale-100"
            leave-to-class="translate-y-[-30px] opacity-0 scale-90"
          >
            <div
              v-for="toast in toasts"
              :key="toast.id"
              class="w-full bg-white shadow-2xl rounded-2xl pointer-events-auto border-3 border-black overflow-hidden"
              style="box-shadow: 6px 6px 0px #000"
            >
              <div class="p-8">
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <!-- Success Icon -->
                    <svg
                      v-if="toast.type === 'success'"
                      class="h-8 w-8 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <!-- Error Icon -->
                    <svg
                      v-else-if="toast.type === 'error'"
                      class="h-8 w-8 text-red-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <!-- Warning Icon -->
                    <svg
                      v-else-if="toast.type === 'warning'"
                      class="h-8 w-8 mt-2 text-yellow-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <!-- Info Icon -->
                    <svg
                      v-else
                      class="h-8 w-8 text-blue-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div class="ml-6 flex-1 pt-1">
                    <p
                      class="text-md font-semibold leading-relaxed"
                      :class="{
                        'text-green-800': toast.type === 'success',
                        'text-red-800': toast.type === 'error',
                        'text-yellow-800': toast.type === 'warning',
                        'text-blue-800': toast.type === 'info',
                      }"
                    >
                      {{ toast.message }}
                    </p>
                  </div>
                  <div class="ml-6 flex-shrink-0 flex">
                    <button
                      @click="removeToast(toast.id)"
                      class="bg-gray-50 rounded-xl inline-flex text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 p-2 transition-colors duration-200"
                    >
                      <span class="sr-only">Close</span>
                      <svg class="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fill-rule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </div>
      </div>
    </Teleport>
  </template>
  
  <script setup lang="ts">
  import { useToast } from "@/composables/useSessionToast";
  
  const { toasts, removeToast } = useToast();
  </script>
  